// src/components/Admin/HistorialPanel.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "./Button";
import Select from "./Select";
import TextInput from "./TextInput";
import FieldLabel from "./FieldLabel";
import EstadoReciboSelect from "./EstadoReciboSelect";
import { API, fetchJson } from "../Utiles/api";
import { exportRowsToExcel } from "../Utiles/excel";
import { fmtMoney } from "../Utiles/helpers";
import { Download, ListChecks, Loader2 } from "lucide-react";

const HistorialPanel = ({ codigoBuscar, setCodigoBuscar, toast, setToast }) => {
  const [loadingHist, setLoadingHist] = useState(false);
  const [historial, setHistorial] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [marcarImprimir, setMarcarImprimir] = useState(true);
  const [cobrando, setCobrando] = useState(false);

  const fmtMoneyLocal =
    typeof window !== "undefined" && window.fmtMoney
      ? window.fmtMoney
      : (n) =>
        Number(n || 0).toLocaleString("es-ES", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 2,
        });

  const cargarLogs = async (codigo) => {
    const code = String(codigo ?? codigoBuscar).trim();
    if (!code) return;
    try {
      setLoadingLogs(true);
      const data = await fetchJson(API.historialLogs(code));
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      setToast?.(e.message || "Error cargando historial unificado");
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const normalizeHistorial = useCallback((raw, codigo) => {
    if (!raw) return { colegiado_codigo: codigo, recibos: [] };
    const recs = Array.isArray(raw.recibos) ? raw.recibos : [];
    return {
      colegiado_codigo: raw.colegiadoCodigo ?? raw.colegiado_codigo ?? codigo,
      nombre: raw.nombre ?? "",
      nif: raw.nif ?? "",
      recibos: recs.map((r) => ({
        id: r.id,
        periodo: r.periodo,
        reqd_colltn_dt: r.reqd_colltn_dt ?? r.reqdColltnDt ?? "",
        importe: r.importe,
        seq_tp: r.seq_tp ?? r.seqTp ?? "",
        mndt_id: r.mndt_id ?? r.mndtId ?? "",
        end_to_end_id: r.end_to_end_id ?? r.endToEndId ?? "",
        resultado_banco: r.resultado_banco ?? r.resultadoBanco ?? "",
        remesable: !!r.remesable,
        colegiado_codigo: r.colegiado_codigo ?? r.colegiadoCodigo,
      })),
    };
  }, []);

  const buscarHistorial = async (codigo) => {
    const code = String(codigo ?? codigoBuscar).trim();
    if (!code) return;
    try {
      setLoadingHist(true);
      const raw = await fetchJson(API.colegiadoHist(code));
      const data = normalizeHistorial(raw, code);
      setHistorial(data);
      await cargarLogs(code);
    } catch (e) {
      setToast?.(e.message || "Error historial");
      setHistorial({ colegiado_codigo: code, nombre: "", nif: "", recibos: [] });
    } finally {
      setLoadingHist(false);
    }
  };

  const setResultadoRecibo = async (recibo, valorUi) => {
    try {
      await fetchJson(API.setResultado(recibo.id, valorUi), { method: "PATCH" });
      setToast?.(`Recibo ${recibo.id}: estado=${valorUi}`);
      setHistorial((h) =>
        h
          ? {
            ...h,
            recibos: h.recibos.map((rc) =>
              rc.id === recibo.id ? { ...rc, resultado_banco: valorUi, resultadoBanco: valorUi } : rc
            ),
          }
          : h
      );
    } catch (e) {
      setToast?.(e.message || "Error actualizando estado");
    }
  };

  const toggleRemesable = async (recibo) => {
    try {
      await fetchJson(`/api/recibos/${recibo.id}/remesable`, {
        method: "PATCH",
        body: JSON.stringify({ remesable: !recibo.remesable }),
      });
      setToast?.(`Recibo ${recibo.id}: remesable=${!recibo.remesable}`);
      setHistorial((h) =>
        h
          ? {
            ...h,
            recibos: h.recibos.map((rc) =>
              rc.id === recibo.id ? { ...rc, remesable: !recibo.remesable } : rc
            ),
          }
          : h
      );
    } catch (e) {
      setToast?.(e.message || "Error actualizando recibo");
    }
  };

  const onKeyDownCodigo = (e) => {
    if (e.key === "Enter") buscarHistorial();
  };

  useEffect(() => {
    if (String(codigoBuscar || "").trim()) buscarHistorial(codigoBuscar);
  }, []);

  const normalizeEstado = (v) =>
    String(v || "").trim().toUpperCase().replace(/\s+/g, "_").replace(/-+/g, "_");

  const pendientes = useMemo(() => {
    const recs = Array.isArray(historial?.recibos) ? historial.recibos : [];
    return recs.filter((r) => {
      const st = normalizeEstado(r.resultado_banco ?? r.resultadoBanco);
      return st === "PENDIENTE" || st === "PENDIENTE_DEVUELTO";
    });
  }, [historial]);

  const totalPendiente = useMemo(
    () => pendientes.reduce((acc, r) => acc + (isNaN(Number(r.importe)) ? 0 : Number(r.importe)), 0),
    [pendientes]
  );

  const imprimirJustificante = (colegiado, recs) => {
    const logoUrl = `${window.location.origin}/logocolegio.png`;
    const fechaStr = new Date().toLocaleString("es-ES");
    const total = recs.reduce((a, r) => a + Number(r.importe || 0), 0);

    const rowsHtml = recs
      .map(
        (r) => `
        <tr>
          <td>#${r.id}</td>
          <td>${r.periodo || ""}</td>
          <td class="right">${Number(r.importe || 0).toLocaleString("es-ES", {
          style: "currency",
          currency: "EUR",
        })}</td>
        </tr>`
      )
      .join("");

    const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Justificante de pago</title>
        <style>
          body{ font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial; margin: 24px; }
          .header{ display:flex; align-items:center; gap:14px; margin-bottom: 10px; }
          .logo{ height: 100px; width:auto; object-fit: contain; }
          h1{ font-size: 18px; margin: 0; }
          h2{ font-size: 14px; margin: 2px 0 0 0; font-weight: 600; }
          .muted{ color:#555; margin-top: 8px; }
          table{ width:100%; border-collapse: collapse; margin-top:12px; font-size: 13px; }
          th, td{ border:1px solid #ddd; padding:8px; text-align:left; }
          th{ background:#f5f5f5; }
          .right{ text-align:right; }
          .total{ font-weight:bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <img id="logo" src="${logoUrl}" class="logo" alt="Logo" />
          <div>
            <h1>Colegio Oficial de Enfermería de Valladolid</h1>
            <h2>Justificante de pago en ventanilla</h2>
          </div>
        </div>

        <div class="muted">
          <div><strong>Colegiado:</strong> ${colegiado?.nombre || ""}</div>
          <div><strong>Código:</strong> ${colegiado?.codigo || ""} &nbsp; <strong>NIF:</strong> ${colegiado?.nif || ""}</div>
          <div><strong>Fecha:</strong> ${fechaStr}</div>
        </div>

        <table>
          <thead>
            <tr><th>ID Recibo</th><th>Periodo</th><th class="right">Importe</th></tr>
          </thead>
          <tbody>
            ${rowsHtml}
            <tr>
              <td colspan="2" class="right total">TOTAL</td>
              <td class="right total">${Number(total).toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</td>
            </tr>
          </tbody>
        </table>

        <script>
          // Espera a que la imagen cargue antes de imprimir
          (function () {
            var img = document.getElementById("logo");
            function goPrint(){ setTimeout(function(){ window.print(); }, 50); }

            if (!img) return goPrint();

            if (img.complete) return goPrint();

            img.onload = goPrint;
            img.onerror = goPrint;
          })();
        </script>
      </body>
    </html>
  `;

    const w = window.open("", "_blank");
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
    }
  };


  const cobrarTodosPendientes = async () => {
    if (!historial || pendientes.length === 0) return;
    if (!window.confirm(`Se marcarán ${pendientes.length} recibo(s) como COBRADO. ¿Continuar?`)) return;

    setCobrando(true);
    try {
      await Promise.all(
        pendientes.map((r) => fetchJson(API.setResultado(r.id, "COBRADO"), { method: "PATCH" }))
      );

      const ids = new Set(pendientes.map((r) => r.id));
      setHistorial((h) =>
        h
          ? {
            ...h,
            recibos: h.recibos.map((rc) =>
              ids.has(rc.id) ? { ...rc, resultado_banco: "COBRADO", resultadoBanco: "COBRADO" } : rc
            ),
          }
          : h
      );

      setToast?.("Recibos pendientes marcados como COBRADOS");

      if (marcarImprimir) {
        const colegiadoInfo = {
          codigo: historial.colegiado_codigo ?? historial.colegiadoCodigo,
          nombre: historial.nombre,
          nif: historial.nif,
        };
        imprimirJustificante(colegiadoInfo, pendientes);
      }
    } catch (e) {
      setToast?.(e.message || "Error al marcar como cobrados");
    } finally {
      setCobrando(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-2xl text-gray-800">Movimientos del Colegiado</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            iconLeft={<Download size={16} />}
            onClick={() => {
              exportRowsToExcel({
                filename: `movimientos_recibos_${historial?.colegiado_codigo || ""}.xlsx`,
                rows: historial?.recibos || [],
                columns: [
                  { header: "ID", value: (r) => r.id },
                  { header: "Periodo", value: (r) => r.periodo },
                  { header: "Fecha cobro", value: (r) => r.reqd_colltn_dt || "" },
                  { header: "Importe", value: (r) => r.importe },
                  { header: "Seq", value: (r) => r.seq_tp || "" },
                  { header: "Mandato", value: (r) => r.mndt_id || "" },
                  { header: "E2E", value: (r) => r.end_to_end_id || "" },
                  { header: "Estado", value: (r) => r.resultado_banco || "" },
                  { header: "Remesable", value: (r) => (r.remesable ? "Sí" : "No") },
                ],
              });
            }}
          >
            Exportar Recibos
          </Button>
          <Button
            variant="outline"
            iconLeft={<Download size={16} />}
            onClick={() => {
              exportRowsToExcel({
                filename: `movimientos_logs_${historial?.colegiado_codigo || ""}.xlsx`,
                rows: logs,
                columns: [
                  { header: "Periodo", value: (l) => l.periodo || "" },
                  {
                    header: "Fecha",
                    value: (l) =>
                      l.cambiado_el || l.cambiadoEl
                        ? new Date(l.cambiado_el ?? l.cambiadoEl).toLocaleString()
                        : "",
                  },
                  { header: "Estado anterior", value: (l) => l.estado_anterior ?? l.estadoAnterior ?? "" },
                  { header: "Estado nuevo", value: (l) => l.estado_nuevo ?? l.estadoNuevo ?? "" },
                  { header: "Recibo", value: (l) => l.recibo_id ?? l.reciboId ?? "" },
                ],
              });
            }}
          >
            Exportar Logs
          </Button>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <FieldLabel label="Código" />
            <TextInput
              className="w-40"
              value={codigoBuscar}
              onChange={(e) => setCodigoBuscar(e.target.value)}
              onKeyDown={onKeyDownCodigo}
              placeholder="1234"
            />
            <Button variant="outline" onClick={() => buscarHistorial()} iconLeft={<ListChecks className="w-4 h-4" />}>
              Buscar
            </Button>
            {loadingHist && (
              <span className="ml-2 text-sm text-gray-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
              </span>
            )}
          </div>

          {historial && (
            <div className="mt-3 text-sm text-gray-700 flex flex-wrap gap-x-6 gap-y-1">
              <span>
                <span className="text-gray-500">Código:</span>{" "}
                <strong>{historial.colegiado_codigo ?? historial.colegiadoCodigo}</strong>
              </span>
              <span>
                <span className="text-gray-500">Nombre:</span>{" "}
                <strong>{historial.nombre || "—"}</strong>
              </span>
              <span>
                <span className="text-gray-500">NIF:</span> <strong>{historial.nif || "—"}</strong>
              </span>
              <span>
                <span className="text-gray-500">Recibos:</span>{" "}
                <strong>{Array.isArray(historial.recibos) ? historial.recibos.length : 0}</strong>
              </span>
            </div>
          )}
        </div>

        <div
          className={
            pendientes.length > 0
              ? "rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3"
              : "rounded-xl border border-gray-200 bg-gray-50 text-gray-700 px-4 py-3"
          }
        >
          <div className="flex items-center justify-between">
            <strong className="text-base tracking-wide">Recibos pendientes</strong>
            <span
              className={
                pendientes.length > 0
                  ? "text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 border border-red-200"
                  : "text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200"
              }
            >
              {pendientes.length}
            </span>
          </div>

          {pendientes.length > 0 ? (
            <>
              <div className="mt-1 text-sm">
                Debe: <span className="font-bold">{fmtMoneyLocal(totalPendiente)}</span>
              </div>
              <ul className="mt-2 text-xs leading-5 max-h-36 overflow-auto pr-1">
                {pendientes.slice(0, 5).map((r) => (
                  <li key={r.id} className="flex justify-between gap-3">
                    <span className="font-mono">#{r.id}</span>
                    <span className="truncate">{r.periodo}</span>
                    <span className="shrink-0">{fmtMoneyLocal(r.importe)}</span>
                  </li>
                ))}
                {pendientes.length > 5 && <li className="italic mt-1">… y {pendientes.length - 5} más</li>}
              </ul>

              <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" className="accent-red-600" checked={marcarImprimir} onChange={(e) => setMarcarImprimir(e.target.checked)} />
                  Imprimir justificante
                </label>
                <Button disabled={cobrando} onClick={cobrarTodosPendientes} className="ml-auto" variant="solid">
                  {cobrando ? "Marcando…" : "Marcar como COBRADOS"}
                </Button>
              </div>
            </>
          ) : (
            <p className="mt-2 text-sm">Sin recibos pendientes.</p>
          )}
        </div>
      </div>

      {/* Logs */}
      <div className="overflow-auto mb-2">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th>Periodo</th><th>Fecha</th><th>Concepto</th><th>Estado nuevo</th><th>Recibo</th>
            </tr>
          </thead>
          <tbody>
            {!loadingLogs && logs.length === 0 && (
              <tr><td colSpan={5} className="text-center text-gray-400 py-6">Sin cambios</td></tr>
            )}
            {logs.map((l, i) => {
              const fecha = l.cambiado_el || l.cambiadoEl;
              const periodo = l.periodo;
              const estadoAnt = l.estado_anterior ?? l.estadoAnterior ?? "(nulo)";
              const estadoNvo = l.estado_nuevo ?? l.estadoNuevo ?? "(nulo)";
              const reciboId = l.recibo_id ?? l.reciboId;
              return (
                <tr key={i} className="border-t">
                  <td>{periodo || ""}</td>
                  <td>{fecha ? new Date(fecha).toLocaleString() : ""}</td>
                  <td className="max-w-[50ch] truncate" title={`De ${estadoAnt} a ${estadoNvo}`}>{`Estado ${estadoAnt} → ${estadoNvo}`}</td>
                  <td><span className="text-xs px-2 py-1 rounded-full bg-gray-100">{(l.estado_nuevo ?? l.estadoNuevo ?? "").toString().toUpperCase()}</span></td>
                  <td className="text-xs text-gray-500">{reciboId ? `#${reciboId}` : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tabla recibos del historial */}
      {historial && (
        <div className="space-y-3 mt-2">
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th>ID</th><th>Periodo</th><th>Fecha cobro</th><th>Importe</th><th>Seq</th><th>Mandato</th><th>E2E</th><th>Estado</th><th>Remesable</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(historial.recibos) ? historial.recibos : []).map((rc) => (
                  <tr key={rc.id} className="border-t">
                    <td>{rc.id}</td>
                    <td>{rc.periodo}</td>
                    <td>{rc.reqd_colltn_dt ?? rc.reqdColltnDt}</td>
                    <td>{fmtMoney(rc.importe)}</td>
                    <td>{rc.seq_tp ?? rc.seqTp}</td>
                    <td className="font-mono text-xs">{rc.mndt_id ?? rc.mndtId}</td>
                    <td className="font-mono text-xs">{rc.end_to_end_id ?? rc.endToEndId ?? ""}</td>
                    <td>
                      <EstadoReciboSelect value={rc.resultado_banco ?? rc.resultadoBanco} onChange={(ui) => setResultadoRecibo(rc, ui)} />
                    </td>
                    <td>
                      <input type="checkbox" checked={!!rc.remesable} onChange={() => toggleRemesable(rc)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistorialPanel;
